import React, { useEffect } from 'react'
import { Formik } from 'formik'
import { Button, Grid, Tab, Tabs } from '@mui/material'
import { RtkQueryHelper } from '../../../api-services/shared/RtkQueryHelper'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'

import ErrorComponent from '../../components/ui-widgets/ErrorComponent'
import LoadingComponent from '../../components/ui-widgets/LoadingComponent'
import { setNotification } from '../../components/ui-widgets/uiWidgetsSlice'
import AssetHolderDisbursementsPage from './disbursements/AssetHolderDisbursementsPage'
import {
  selectTabIndex,
  setTabIndex
} from './editAssetHolderSlice'
import { useUpdateAssetHolderMutation } from '../../../api-services/asset-holdings/assetHolderApi'
import { validateAssetHolderUpdateDto } from '../../../api-schema-types/asset-holdings/AssetHolderCrudDtos'
import { type AssetHolderUpdateDto } from '../../../api-schema-types/asset-holdings/AssetHolderCrudDtos'
import EditAssetHolderDetailsAndRolesView from './EditAssetHolderDetailsAndRolesView'
import EditAssetHolderKycView from './EditAssetHolderKycView'
import EditAssetHolderCoasView from './EditAssetHolderCoasView'
import EditAssetHolderOrdersView from './EditAssetHolderOrdersView'
import EditAssetHolderInstructionsView from './EditAssetHolderInstructionsView'
import EditAssetHolderFinanceDetailsView from './EditAssetHolderFinanceDetailsView'
import ProfileUpdatesComponent from './ProfileUpdatesComponent'
import ViewPortfolioSettlements from './ViewPortfolioSettlements'

export interface EditAssetHolderFormProps {
  assetHolder: AssetHolderUpdateDto
  triggerRefetch: () => void
}

export interface EditAssetHolderProps {
  assetHolder: AssetHolderUpdateDto
}

const EditAssetHolderForm: React.FC<EditAssetHolderFormProps> = ({ assetHolder, triggerRefetch }: EditAssetHolderFormProps) => {
  const dispatch = useAppDispatch()
  const tabIndex = useAppSelector(selectTabIndex)

  // ================================================================================================================
  //  FORM SETUP
  // ================================================================================================================

  const updateAssetHolderOnSubmit = async (values: AssetHolderUpdateDto): Promise<void> => {
    const updateAssetHolder = { ...values }

    await apiUpdateAssetHolderTrigger({ ...updateAssetHolder })
    // dispatch()
  }

  const handleCancel = async (): Promise<void> => {
    dispatch(setNotification({
      open: true,
      severity: 'warning',
      message: 'Edit action cancelled. Project has been reloaded.'
    }))
    triggerRefetch()
  }

  // ================================================================================================================
  //  UI Handlers
  // ================================================================================================================

  const handleTabSelected = (event: React.ChangeEvent<unknown>, newValue: number): void => {
    dispatch(setTabIndex(newValue))
  }

  // const toggleOverrideFields = (): void => {
  //   dispatch(setOverrideLockedFields(!overrideLockedFields))
  // }

  // ================================================================================================================
  //  FORM MUTATION + RESULT
  // ================================================================================================================

  const [apiUpdateAssetHolderTrigger, updateResult] = useUpdateAssetHolderMutation()

  useEffect(() => {
    if (updateResult.isSuccess) {
      dispatch(setNotification({ open: true, severity: 'success', message: 'Asset Holder updated successfully' }))
    }

    if (updateResult.isError) {
      dispatch(setNotification({
        open: true,
        severity: 'error',
        message: RtkQueryHelper.mapResponseError(updateResult.error)
      }))
    }
  }, [updateResult.isSuccess, updateResult.isError])

  return (
    <>
      <Formik
        initialValues={assetHolder}
        validationSchema={validateAssetHolderUpdateDto}
        onSubmit={async (values, actions) => {
          await updateAssetHolderOnSubmit(values)
        }}
      >
        {formikProps => (

          <form
            autoComplete={'off'}
            autoCorrect={'off'}
            onSubmit={formikProps.handleSubmit}
          >

            {!formikProps.isValid && (
              <Grid item md={12} sx={{ mt: 3 }}>
                <ErrorComponent title={'Form validation error'} message={JSON.stringify(formikProps.errors)}/>
              </Grid>
            )}

            {updateResult.isLoading && <LoadingComponent/>}

              <Tabs value={tabIndex} onChange={handleTabSelected} indicatorColor="primary" textColor="primary" centered>
              <Tab label="Details & Roles"/>
              <Tab label="Settlement"/>
              <Tab label="KYC Details"/>
              <Tab label="Cell Owner Agreements" />
              <Tab label="Orders"/>
              <Tab label="Finance Details"/>
              <Tab label="Instructions"/>
              <Tab label="Disbursements"/>
              <Tab label="Profile Updates"/>
            </Tabs>

            {tabIndex === 0 && (
              <EditAssetHolderDetailsAndRolesView assetHolder={assetHolder}/>
            )}

            {tabIndex === 1 && (
              <ViewPortfolioSettlements assetHolder={assetHolder}/>
            )}

            {tabIndex === 2 && (
              <EditAssetHolderKycView assetHolder={assetHolder}/>
            )}

            {tabIndex === 3 && (
              <EditAssetHolderCoasView assetHolder={assetHolder}/>
            )}

            {tabIndex === 4 && (
              <EditAssetHolderOrdersView assetHolder={assetHolder}/>
            )}

            {tabIndex === 5 && (
              <EditAssetHolderFinanceDetailsView assetHolder={assetHolder}/>
            )}

            {tabIndex === 6 && (
              <EditAssetHolderInstructionsView assetHolder={assetHolder}/>
            )}

            {tabIndex === 7 && (
              <>
                <AssetHolderDisbursementsPage assetHolder={assetHolder}/>
              </>

            )}

            {tabIndex === 8 && (
              <>
                <ProfileUpdatesComponent assetHolder={assetHolder}/>
              </>

            )}

            <Grid container spacing={4}>
              <Grid item lg={9} sx={{ mt: 3 }}>
                <Button variant={'contained'} disabled={!formikProps.isValid} type={'submit'} sx={{ mr: 3 }} >
                  Update Asset Holder
                </Button>
                <Button type={'button'} variant={'text'} onClick={handleCancel}>
                  Cancel
                </Button>
              </Grid>
              <Grid item lg={3} sx={{ mt: 3, textAlign: 'right' }}>
                {/* <Button type={'button'} variant={'contained'} color={'warning'} */}
                {/*        disabled={true} */}
                {/*        onClick={toggleOverrideFields}> */}
                {/*  Toggle lock overrides */}
                {/* </Button> */}
              </Grid>
            </Grid>

          </form>

        )}
      </Formik>
    </>
  )
}

export default EditAssetHolderForm
